import type { AGGError } from '@atlassian/relay-errors';

/** Relay response payload, including numeric indices in GraphQL response paths. */
export type RelayGraphQLError = Omit<AGGError, 'path' | 'locations'> & {
	path?: ReadonlyArray<string | number>;
	locations?: ReadonlyArray<{ line: number; column: number }>;
	originalError?: Error | null;
};

/** Payload conveniences retained for existing error handling and logging callers. */
export type GraphQLErrorPayload = RelayGraphQLError &
	Error & {
		name: string;
		extensions: NonNullable<AGGError['extensions']>;
		path: RelayGraphQLError['path'];
		locations: RelayGraphQLError['locations'];
		originalError: Error | null | undefined;
		stack?: string;
		toJSON(): RelayGraphQLError;
	};

type GraphQLErrorOptions = {
	path?: RelayGraphQLError['path'] | null;
	locations?: RelayGraphQLError['locations'];
	severity?: RelayGraphQLError['severity'];
	originalError?: Error | null;
	extensions?: AGGError['extensions'] | null;
};

/** Creates a Relay payload without importing GraphQL or allocating a GraphQLError. */
export function createGraphQLError(
	message: string,
	options: GraphQLErrorOptions = {},
): GraphQLErrorPayload {
	const originalError = options.originalError;
	const inheritedExtensions =
		originalError &&
		'extensions' in originalError &&
		typeof originalError.extensions === 'object' &&
		originalError.extensions !== null
			? originalError.extensions
			: {};
	const extensions = options.extensions ?? inheritedExtensions;
	const payload: RelayGraphQLError = {
		message,
		...(options.severity && { severity: options.severity }),
		...(options.locations && { locations: options.locations }),
		...(options.path && { path: options.path }),
		...(Object.keys(extensions).length > 0 && { extensions }),
	};
	// Keep compatibility properties out of server-payload comparisons and serialization.
	const error = Object.defineProperties(payload, {
		name: { value: 'GraphQLError', configurable: true },
		extensions: {
			value: extensions,
			enumerable: Object.keys(extensions).length > 0,
			configurable: true,
		},
		path: {
			value: options.path ?? undefined,
			enumerable: options.path != null,
			configurable: true,
		},
		locations: {
			value: options.locations,
			enumerable: options.locations != null,
			configurable: true,
		},
		originalError: { value: originalError, configurable: true },
		stack: { value: originalError?.stack, configurable: true },
		toString: { value: () => message, configurable: true },
		toJSON: { value: () => ({ ...payload }), configurable: true },
	}) as GraphQLErrorPayload;
	return error;
}

/** Use an Error when throwing; source.errors retains the underlying Relay response payload. */
export function createGraphQLException(
	message: string,
	options: GraphQLErrorOptions = {},
): Error & RelayGraphQLError & { source: { errors: ReadonlyArray<RelayGraphQLError> } } {
	const payload = createGraphQLError(message, options);
	return Object.assign(new Error(message, { cause: options.originalError }), payload, {
		name: 'GraphQLError',
		originalError: options.originalError,
		source: { errors: [payload] },
	});
}

/** Accepts Relay payloads without requiring a GraphQL error constructor. */
export function isRelayGraphQLError(value: unknown): value is RelayGraphQLError {
	return (
		typeof value === 'object' &&
		value !== null &&
		'message' in value &&
		typeof value.message === 'string' &&
		!('graphQLErrors' in value) &&
		!('networkError' in value) &&
		(!(value instanceof Error) || value.name === 'GraphQLError')
	);
}

export const isRelayError = (error: unknown): error is RelayError => {
	return (
		typeof error === 'object' &&
		error !== null &&
		(Object.prototype.hasOwnProperty.call(error, 'graphQLErrors') ||
			Object.prototype.hasOwnProperty.call(error, 'networkError'))
	);
};

/** Native Relay failure with the field names still consumed by Confluence callers. */
export class RelayError extends Error {
	graphQLErrors: ReadonlyArray<RelayGraphQLError>;
	networkError: Error | null;
	extraInfo: any;
	source?: { errors: ReadonlyArray<RelayGraphQLError> };

	constructor(options: {
		graphQLErrors?: ReadonlyArray<RelayGraphQLError> | RelayGraphQLError;
		networkError?: Error | null;
		errorMessage?: string;
		extraInfo?: any;
	}) {
		const incoming = options.graphQLErrors;
		const payloads = incoming == null ? [] : 'message' in incoming ? [incoming] : incoming;
		// Preserve Relay payload identity, including metadata attached by error processors.
		// Some legacy callers pass a single payload or null entries instead of an array.
		const graphQLErrors = payloads.filter((error) => error != null);
		const networkError = options.networkError ?? null;
		const message =
			options.errorMessage ||
			[
				...graphQLErrors.map((error) => `GraphQL error: ${error.message}`),
				...(networkError ? [`Network error: ${networkError.message}`] : []),
			].join('\n');
		super(options.errorMessage, {
			cause: networkError ?? graphQLErrors[0]?.originalError ?? graphQLErrors[0],
		});
		this.graphQLErrors = graphQLErrors;
		this.networkError = networkError;
		// As in the legacy wrapper, generated messages are enumerable; explicit messages
		// keep the native Error property descriptor established by super().
		this.message = message;
		this.extraInfo = options.extraInfo;
		Object.defineProperty(this, 'source', {
			value: { errors: graphQLErrors },
			configurable: true,
			writable: true,
			enumerable: false,
		});
		Object.setPrototypeOf(this, RelayError.prototype);
	}
}
